import React, {useCallback, useMemo} from 'react';

import {CourseProgressCardsSkeleton} from '@udemy/browse-course';
import {TrackImpression, generateTrackingId} from '@udemy/event-tracking';
import {useI18n} from '@udemy/i18n';
import {Button} from '@udemy/react-core-components';
import {
    AcceleratorsTab,
    AssessmentsTab,
    CertificationPreparationTab,
    JBIAISummary,
    LabsTab,
    LectureProgressTab,
    TrackedCurriculumItemCtaCard,
    TrackedJBIVideoCard,
} from '@udemy/react-my-learning-unit';

import {MyLearningUnitSection as SharedMyLearningUnitSection} from 'src/components/my-learning-unit-section/my-learning-unit-section.react-component';
import {COURSE_RECOMMENDATIONS_SECTION_ID} from 'src/features/constants';
import {useJbiCourseDetails} from 'src/hooks/use-jbi-course-details';
import {useJbiEnhancedLectureUnit} from 'src/hooks/use-jbi-enhanced-lecture-unit';
import {useShowAtRiskSurveyFeature} from 'src/hooks/use-show-at-risk-survey-feature';
import {
    asEnrollmentWithNextItem,
    resolveTrackedJbiCard,
    type TrackedJbiCardConfig,
} from 'src/lib/jbi-enhanced-lecture-card';
import {UserEnrollmentsData} from 'src/types/page-props';

import {trackAtRiskHelpExperimentImpression} from './at-risk-feedback-modal/events';
import styles from './my-learning-unit-section.module.less';

const JBI_CARD_TEST_IDS: Record<TrackedJbiCardConfig['type'], string> = {
    video: 'jbi-video-card-section',
    summary: 'jbi-summary-card-section',
    curriculum: 'jbi-curriculum-card-section',
};

interface MyLearningUnitSectionProps {
    isPaidSubscriber: boolean;
    userEnrollments?: UserEnrollmentsData;
    enableLabsInPersonalPlan?: boolean;
    enableAssessmentsInPersonalPlan?: boolean;
    userHasUBProAccess?: boolean;
    onOpenAtRiskModal?: () => void;
}

// Memoized: this embeds an autoplaying JBI video player, and sibling state elsewhere
// on the page (e.g. the at-risk survey modal's open/closed state) must not cause it to
// re-render — a third-party mobx-react Provider inside the video player throws if it
// re-evaluates its store value while mounted. Requires onOpenAtRiskModal to be a
// referentially stable callback (see useAtRiskSurveyTrigger) for this to actually help.
export const MyLearningUnitSection = React.memo(function MyLearningUnitSection({
    isPaidSubscriber,
    userEnrollments,
    enableLabsInPersonalPlan = false,
    enableAssessmentsInPersonalPlan = false,
    userHasUBProAccess = false,
    onOpenAtRiskModal,
}: MyLearningUnitSectionProps) {
    const {gettext} = useI18n();
    const {variant, isLoading} = useJbiEnhancedLectureUnit();
    const {assignment: atRiskHelpAssignment} = useShowAtRiskSurveyFeature();

    const handleAtRiskTriggerImpression = useCallback(() => {
        trackAtRiskHelpExperimentImpression(atRiskHelpAssignment);
    }, [atRiskHelpAssignment]);

    const handleAtRiskTriggerClick = useCallback(() => {
        onOpenAtRiskModal?.();
    }, [onOpenAtRiskModal]);

    const courses = useMemo(
        () =>
            (
                userEnrollments?.me?.enrollments?.courseEnrollmentsPaged?.courseEnrollments ?? []
            ).filter((enrollment) => Boolean(enrollment?.course?.nextCurriculumItem)),
        [userEnrollments],
    );
    const accelerators =
        userEnrollments?.me?.enrollments?.careerAcceleratorEnrollments?.enrollments ?? [];
    const learningPaths = userEnrollments?.me?.enrollments?.learningPathEnrollments ?? [];
    const certifications = userEnrollments?.me?.enrollments?.badgeEnrollments ?? [];

    const hasContent = Boolean(
        courses.length > 0 ||
            accelerators.length > 0 ||
            learningPaths.length > 0 ||
            certifications.length > 0,
    );

    const primaryEnrollment = useMemo(() => asEnrollmentWithNextItem(courses[0]), [courses]);

    const {data: courseDetails} = useJbiCourseDetails(primaryEnrollment?.course.id, variant);

    const trackedJbiCard = useMemo(
        () =>
            resolveTrackedJbiCard({
                isLoading,
                variant,
                enrollment: primaryEnrollment,
                generateId: generateTrackingId,
                courseDetails: courseDetails ?? undefined,
            }),
        [isLoading, primaryEnrollment, variant, courseDetails],
    );

    const shouldShowDefaultMyLearningSection =
        variant === 'control' || (variant === 'video' && !primaryEnrollment);

    const learningTabs = useMemo(() => {
        if (!shouldShowDefaultMyLearningSection) return null;

        const tabs = [];
        let tabIndex = 0;

        // Pass courses from SSR data so LectureProgressTab.load() returns immediately
        // without making an extra API call. When no courses exist, undefined triggers
        // the built-in "Explore our course collection" empty-state banner.
        tabs.push(
            new LectureProgressTab(
                tabIndex++,
                'Courses',
                <CourseProgressCardsSkeleton />,
                gettext,
                courses.length > 0 ? courses : undefined,
                undefined,
                undefined,
                () => {
                    document
                        .getElementById(COURSE_RECOMMENDATIONS_SECTION_ID)
                        ?.scrollIntoView({behavior: 'smooth'});
                },
            ),
        );

        if (accelerators.length > 0) {
            tabs.push(
                new AcceleratorsTab(
                    tabIndex++,
                    'Career Accelerators',
                    <CourseProgressCardsSkeleton />,
                    gettext,
                    undefined, // UserEnrollmentsData lacks slug/career/heroImage — let tab self-fetch
                    false,
                    isPaidSubscriber,
                ),
            );
        }

        tabs.push(
            new CertificationPreparationTab(
                tabIndex++,
                'Certification preparation',
                <CourseProgressCardsSkeleton />,
                gettext,
            ),
        );

        if (enableAssessmentsInPersonalPlan) {
            tabs.push(
                new AssessmentsTab(
                    tabIndex++,
                    'Assessments',
                    <CourseProgressCardsSkeleton />,
                    gettext,
                    isPaidSubscriber,
                ),
            );
        }

        if ((isPaidSubscriber && enableLabsInPersonalPlan) || userHasUBProAccess) {
            tabs.push(
                new LabsTab(
                    tabIndex++,
                    'Labs',
                    <CourseProgressCardsSkeleton />,
                    gettext,
                    isPaidSubscriber,
                    userHasUBProAccess,
                ),
            );
        }

        return tabs;
    }, [
        shouldShowDefaultMyLearningSection,
        gettext,
        courses,
        accelerators.length,
        isPaidSubscriber,
        enableLabsInPersonalPlan,
        enableAssessmentsInPersonalPlan,
        userHasUBProAccess,
    ]);

    const title = useMemo(() => {
        if (!shouldShowDefaultMyLearningSection) return '';

        return isPaidSubscriber && hasContent
            ? gettext('Pick up where you left off')
            : gettext("Let's start learning");
    }, [shouldShowDefaultMyLearningSection, gettext, isPaidSubscriber, hasContent]);

    // Always render for paid subscribers; non-paid users only see the unit if they
    // have content or a feature that requires it (cert prep, labs, UB Pro).
    const shouldShowMyLearningSection =
        isPaidSubscriber || Boolean(hasContent || (enableLabsInPersonalPlan && userHasUBProAccess));

    if (!shouldShowMyLearningSection) return null;

    if (isLoading) {
        return (
            <div className="component-margin" data-testid="my-learning-unit-loading">
                <CourseProgressCardsSkeleton />
            </div>
        );
    }

    // Rendered below the JBI card when one resolves, otherwise below the default
    // my-learning section — the survey link is independent of the JBI card experiment.
    const atRiskTrigger = onOpenAtRiskModal ? (
        <TrackImpression trackFunc={handleAtRiskTriggerImpression}>
            <div className={styles['at-risk-trigger']} data-testid="at-risk-jbi-trigger">
                <Button
                    udStyle="link"
                    size="medium"
                    className={styles['at-risk-trigger-button']}
                    onClick={handleAtRiskTriggerClick}
                >
                    {gettext("How's your learning going?")}
                </Button>
            </div>
        </TrackImpression>
    ) : null;

    if (trackedJbiCard) {
        return (
            <>
                <div
                    className="component-margin"
                    data-testid={JBI_CARD_TEST_IDS[trackedJbiCard.type]}
                >
                    {trackedJbiCard.type === 'video' ? (
                        <TrackedJBIVideoCard
                            {...trackedJbiCard.cardProps}
                            {...trackedJbiCard.trackingProps}
                        />
                    ) : trackedJbiCard.type === 'summary' ? (
                        <JBIAISummary {...trackedJbiCard.cardProps} />
                    ) : (
                        <TrackedCurriculumItemCtaCard
                            {...trackedJbiCard.cardProps}
                            {...trackedJbiCard.trackingProps}
                        />
                    )}
                </div>
                {atRiskTrigger}
            </>
        );
    }

    if (!shouldShowDefaultMyLearningSection || !learningTabs) {
        return atRiskTrigger;
    }

    return (
        <>
            <SharedMyLearningUnitSection
                tabs={learningTabs}
                title={title}
                url="/home/my-courses/"
            />
            {atRiskTrigger}
        </>
    );
});
